import classNames from 'classnames'
import moment from 'moment'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'

import { ReactComponent as AISparkleIcon } from 'src/assets/svg/ai-sparkle.svg'
import { ReactComponent as WalletIcon } from 'src/assets/svg/wallet.svg'
import { UpgradePlanModal } from 'src/components/UpgradePlanModal'
import {
  AD_ANALYSIS_LIBRARY_ROUTE,
  AD_ANALYSIS_OVERVIEW_ROUTE,
  AD_ANALYSIS_PERFORMANCE_ROUTE,
  AI_ASSISTANT_ROUTE,
  AI_LISTENING_ROUTE,
  CAMPAIGNS_ROUTE,
  CAMPAIGN_DETAILS_ROUTE,
  CONTENT_ANALYSIS_DETAIL_ROUTE,
  CONTENT_ANALYSIS_ROUTE,
  HOME_ROUTE,
  INBOX_ANALYSIS_DETAIL_ROUTE,
  INBOX_ANALYSIS_ROUTE,
  KEYWORD_DETAILS_ROUTE,
  KEYWORDS_ROUTE,
  PROJECT_DETAILS_ROUTE,
  REVIEW_ANALYSIS_ROUTE,
} from 'src/constants/routes'
import { useAIAssistant } from 'src/contexts/AIAssistantContext'
import { useDeleteAiChatConversationMutation } from 'src/reactQuery/useDeleteAiChatConversationMutation'
import { useGetAiChatConversationsQuery } from 'src/reactQuery/useGetAiChatConversationsQuery'
import { useGetCreditsQuery } from 'src/reactQuery/useGetCreditsQuery'
import { useGetTrialStatusQuery } from 'src/reactQuery/useGetTrialStatusQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'

import styles from './topBar.module.scss'
import { TopBarDatePicker } from './TopBarDatePicker'
import { UserDropDown } from './UserDropDown'

export const TopBar: FC = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isCreditsDropdownOpen, setIsCreditsDropdownOpen] = useState(false)
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const navigate = useNavigate()
  const {
    clearConversation,
    conversationId,
    hasConversation,
    setConversationId,
    setHasConversation,
  } = useAIAssistant()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const creditsDropdownRef = useRef<HTMLDivElement>(null)

  // Fetch conversations list
  const { data: conversationsData, isLoading: isLoadingConversations } =
    useGetAiChatConversationsQuery()
  const conversations = conversationsData?.conversations || []

  // Fetch user data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // Fetch credits
  const { data: creditsResponse } = useGetCreditsQuery()
  const creditsData = creditsResponse?.data
  // A question is billed as two messages; read the cost from the API rather
  // than repeating the number, so the panel cannot drift from the backend.
  const costPerQuestion =
    creditsData?.costPerInteraction ??
    (creditsData?.costPerMessage ? creditsData.costPerMessage * 2 : 10)

  // Fetch user features to check disabled states
  const { data: userFeatures } = useGetUserFeaturesQuery()
  const inboxEnabled = userFeatures?.data?.inboxAnalysisEnabled ?? true
  const contentEnabled = userFeatures?.data?.contentAnalysisEnabled ?? true
  const reviewEnabled = userFeatures?.data?.reviewAnalysisEnabled ?? true
  const aiListeningEnabled = userFeatures?.data?.aiListeningEnabled ?? true
  const adsLibraryEnabled = userFeatures?.data?.adsLibraryEnabled ?? true
  const adsPerformanceEnabled =
    userFeatures?.data?.adsPerformanceEnabled ?? true
  // Overview is enabled if either ad library or ad performance is enabled
  const adsOverviewEnabled = adsLibraryEnabled || adsPerformanceEnabled

  // Fetch trial status
  const { data: trialStatusResponse } = useGetTrialStatusQuery()
  const trialData = trialStatusResponse?.data
  const showTrial = !!trialData?.isTrial
  // If backend says daysRemaining=0 but isExpired=false, it means today is the last day
  // (endsAt midnight UTC hasn't passed yet locally) — show 1 instead of 0.
  // Otherwise trust the backend value directly.
  const displayDaysRemaining =
    !trialData?.isExpired && trialData?.daysRemaining === 0
      ? 1
      : trialData?.daysRemaining ?? 0
  const isTrialExpired = !!trialData?.isExpired

  // Delete conversation mutation
  const deleteConversationMutation = useDeleteAiChatConversationMutation({
    onSuccess: (deletedId) => {
      // Only the conversation actually deleted may close the open one —
      // comparing against "is anything open" threw the user out of a
      // conversation whenever they deleted a different one from the list.
      if (deletedId === conversationId) {
        clearConversation()
      }
    },
  })

  const handleConversationClick = (id: string) => {
    setConversationId(id)
    setHasConversation(true)
    setIsDropdownOpen(false)
  }

  const handleDeleteConversation = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteConversationMutation.mutate(id)
  }

  const handleNewConversation = () => {
    clearConversation()
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
      if (
        creditsDropdownRef.current &&
        !creditsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCreditsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isHomeRoute = useMatch({
    end: true,
    path: HOME_ROUTE,
  })

  const isProjectDetailsRoute = useMatch({
    end: true,
    path: PROJECT_DETAILS_ROUTE,
  })

  const isCampaignRoute = useMatch({
    end: true,
    path: CAMPAIGNS_ROUTE,
  })

  const isCampaignDetailRoute = useMatch({
    end: true,
    path: CAMPAIGN_DETAILS_ROUTE,
  })

  const isKeywordRoute = useMatch({
    end: true,
    path: KEYWORDS_ROUTE,
  })

  const isKeywordDetailRoute = useMatch({
    end: true,
    path: KEYWORD_DETAILS_ROUTE,
  })

  const isInboxAnalysisRoute = useMatch({
    end: true,
    path: INBOX_ANALYSIS_ROUTE,
  })

  const isInboxAnalysisDetailRoute = useMatch({
    end: true,
    path: INBOX_ANALYSIS_DETAIL_ROUTE,
  })

  const isContentAnalysisRoute = useMatch({
    end: true,
    path: CONTENT_ANALYSIS_ROUTE,
  })

  const isContentAnalysisDetailRoute = useMatch({
    end: true,
    path: CONTENT_ANALYSIS_DETAIL_ROUTE,
  })

  const isReviewAnalysisRoute = useMatch({
    end: false,
    path: REVIEW_ANALYSIS_ROUTE,
  })

  // Every AI Listening screen (brand, competitors, prompts, deep-dive) is
  // date-ranged.
  const isAiListeningRoute = useMatch({
    end: false,
    path: AI_LISTENING_ROUTE,
  })

  const isAdAnalysisOverviewRoute = useMatch({
    end: true,
    path: AD_ANALYSIS_OVERVIEW_ROUTE,
  })

  const isAdAnalysisLibraryRoute = useMatch({
    end: true,
    path: AD_ANALYSIS_LIBRARY_ROUTE,
  })

  const isAdAnalysisPerformanceRoute = useMatch({
    end: true,
    path: AD_ANALYSIS_PERFORMANCE_ROUTE,
  })

  const isAIAssistantRoute = useMatch({
    end: true,
    path: AI_ASSISTANT_ROUTE,
  })

  const isDisabledPage =
    (!!isReviewAnalysisRoute && !reviewEnabled) ||
    (!!isAiListeningRoute && !aiListeningEnabled) ||
    (!!isAdAnalysisOverviewRoute && !adsOverviewEnabled) ||
    (!!isAdAnalysisLibraryRoute && !adsLibraryEnabled) ||
    (!!isAdAnalysisPerformanceRoute && !adsPerformanceEnabled) ||
    (!!isInboxAnalysisRoute && !inboxEnabled) ||
    (!!isContentAnalysisRoute && !contentEnabled)

  const isDatePickerShown =
    !isDisabledPage &&
    (isHomeRoute ||
      isProjectDetailsRoute ||
      isCampaignRoute ||
      isCampaignDetailRoute ||
      isKeywordRoute ||
      isKeywordDetailRoute ||
      isInboxAnalysisRoute ||
      isInboxAnalysisDetailRoute ||
      isContentAnalysisRoute ||
      isContentAnalysisDetailRoute ||
      !!isAiListeningRoute ||
      !!isReviewAnalysisRoute ||
      !!isAdAnalysisPerformanceRoute)

  return (
    <div
      className={classNames(styles.topBar, {
        [styles.scrolled]: isScrolled,
      })}
    >
      {isAIAssistantRoute && (
        <div className={styles.leftButtons}>
          <div className={styles.recentConversationsWrapper} ref={dropdownRef}>
            <button
              className={classNames(styles.recentConversationsButton, {
                [styles.recentConversationsButtonOpen]: isDropdownOpen,
              })}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              type="button"
            >
              Recent Conversations
              <svg
                className={classNames(styles.dropdownArrow, {
                  [styles.dropdownArrowOpen]: isDropdownOpen,
                })}
                fill="none"
                height="20"
                viewBox="0 0 20 20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className={styles.recentConversationsDropdown}>
                {isLoadingConversations ? (
                  <div className={styles.dropdownLoading}>
                    <span className={styles.loadingDot} />
                    <span className={styles.loadingDot} />
                    <span className={styles.loadingDot} />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className={styles.noConversations}>
                    No conversations yet
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <div
                      className={classNames(styles.conversationItem, {
                        [styles.conversationItemActive]:
                          conversation.id === conversationId,
                      })}
                      key={conversation.id}
                      onClick={() => handleConversationClick(conversation.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <span className={styles.conversationTitle}>
                        {moment(conversation.createdAt).format(
                          'MMM D, YYYY h:mm A'
                        )}
                      </span>
                      <button
                        className={styles.deleteButton}
                        disabled={deleteConversationMutation.isPending}
                        onClick={(e) =>
                          handleDeleteConversation(e, conversation.id)
                        }
                        type="button"
                      >
                        <svg
                          fill="none"
                          height="24"
                          viewBox="0 0 24 24"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998"
                            stroke="#86949D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97"
                            stroke="#86949D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M18.8504 9.14001L18.2004 19.21C18.0904 20.78 18.0004 22 15.2104 22H8.79039C6.00039 22 5.91039 20.78 5.80039 19.21L5.15039 9.14001"
                            stroke="#86949D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M10.3301 16.5H13.6601"
                            stroke="#86949D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M9.5 12.5H14.5"
                            stroke="#86949D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            className={classNames(styles.newConversationButton, {
              [styles.newConversationButtonActive]: hasConversation,
            })}
            disabled={!hasConversation}
            onClick={handleNewConversation}
            type="button"
          >
            Start a New Conversation
            <svg
              fill="none"
              height="18"
              viewBox="0 0 18 18"
              width="18"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity={hasConversation ? '0.8' : '0.4'}>
                <path
                  d="M8.25 1.5H6.75C3 1.5 1.5 3 1.5 6.75V11.25C1.5 15 3 16.5 6.75 16.5H11.25C15 16.5 16.5 15 16.5 11.25V9.75"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.2"
                />
                <path
                  d="M12.0304 2.26495L6.1204 8.17495C5.8954 8.39995 5.6704 8.84245 5.6254 9.16495L5.3029 11.4224C5.1829 12.2399 5.7604 12.8099 6.5779 12.6974L8.8354 12.3749C9.1504 12.3299 9.5929 12.1049 9.8254 11.8799L15.7354 5.96995C16.7554 4.94995 17.2354 3.76495 15.7354 2.26495C14.2354 0.764945 13.0504 1.24495 12.0304 2.26495Z"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.2"
                />
                <path
                  d="M11.1826 3.11255C11.6851 4.90505 13.0876 6.30755 14.8876 6.81755"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.2"
                />
              </g>
            </svg>
          </button>
        </div>
      )}

      {isDatePickerShown && <TopBarDatePicker />}

      <div className={styles.right}>
        {/* Trial notification container */}
        {showTrial && (
          <div className={styles.trialContainer}>
            <div className={styles.trialInfo}>
              <span className={styles.trialLabel}>
                Trial Ending<span className={styles.trialColon}>:</span>
              </span>
              <span className={styles.trialDays}>
                {displayDaysRemaining} Day
                {displayDaysRemaining !== 1 ? 's' : ''} Remaining
              </span>
            </div>
            {!isTrialExpired && (
              <button
                className={styles.activatePlanButton}
                onClick={() => setIsTrialModalOpen(true)}
                type="button"
              >
                Activate Plan
              </button>
            )}
          </div>
        )}

        {isAIAssistantRoute ? (
          <div className={styles.creditsWrapper} ref={creditsDropdownRef}>
            <button
              className={styles.creditsButton}
              onClick={() => setIsCreditsDropdownOpen(!isCreditsDropdownOpen)}
              type="button"
            >
              <WalletIcon className={styles.walletIcon} />
              <span className={styles.creditsNumber}>
                {creditsData?.available ?? '-'}
              </span>
              <span className={styles.creditsText}>Credits Left</span>
              <svg
                fill="none"
                height="20"
                viewBox="0 0 20 20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="7.5"
                  stroke="#25262D"
                  strokeWidth="1.2"
                />
                <path
                  d="M10 13.5V9"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeWidth="1.2"
                />
                <circle cx="10" cy="6.5" fill="#25262D" r="0.75" />
              </svg>
            </button>

            {isCreditsDropdownOpen && (
              <div className={styles.creditsDropdown}>
                <div className={styles.creditsDropdownTitle}>Details:</div>
                <div className={styles.creditsRow}>
                  <span className={styles.creditsLabel}>Daily Credits:</span>
                  <span className={styles.creditsValue}>
                    {creditsData?.dailyLimit ?? 100}
                  </span>
                </div>
                <div className={styles.creditsRow}>
                  <span className={styles.creditsLabel}>
                    Cost per Question:
                  </span>
                  <span className={styles.creditsValue}>{costPerQuestion}</span>
                </div>
                <div className={styles.creditsRow}>
                  <span className={styles.creditsLabel}>Used Today:</span>
                  <span className={styles.creditsValueBold}>
                    {(() => {
                      const questions = creditsData
                        ? Math.floor(
                            creditsData.totalUsedToday / costPerQuestion
                          )
                        : 0
                      const credits = creditsData
                        ? creditsData.totalUsedToday
                        : 0
                      return `${questions} ${questions === 1 ? 'Question' : 'Questions'} (${credits} Credits)`
                    })()}
                  </span>
                </div>
                <div className={styles.creditsRow}>
                  <span className={styles.creditsLabel}>Next Reset:</span>
                  <span className={styles.creditsValueBold}>
                    {creditsData?.resetsAt
                      ? moment(creditsData.resetsAt).format('MMM D, HH:mm')
                      : 'Tomorrow, 00:00'}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <button
              className={styles.aiButton}
              onClick={() => navigate(AI_ASSISTANT_ROUTE)}
              type="button"
            >
              <AISparkleIcon className={styles.aiIcon} />
              AI Assistant
            </button>
          </>
        )}

        {/* Temporarily hidden */}
        {/* <span>
          <img alt="" src={NotificationIcon} />
        </span> */}

        <UserDropDown />
      </div>

      {/* Trial Expired Modal */}
      <UpgradePlanModal
        description="If you'd like to activate a Custom Plan, please contact our Support team."
        isOpen={isTrialModalOpen}
        setIsOpen={setIsTrialModalOpen}
        title="Contact Support"
      />
    </div>
  )
}
